// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import styles from '../styles/gameStyles';
import {
  currencyManager,
  getMarketIndexChartData,
  getTrendIcon,
  getTrendColor,
  MarketAnalysis,
} from '../collection';
import {
  hasDebt,
  getDebtSummary,
  DebtStatus,
} from '../collection/investment';
import { FX_PAIRS } from '../collection/investment/data/fxPairs';
import AccountModal from '../components/AccountModal';

/**
 * MenuScreen - メインメニュー画面
 *
 * @param {Object} props
 * @param {boolean} props.isMobile - モバイル表示かどうか
 * @param {Object|null} props.playerData - プレイヤーデータ
 * @param {Function} props.updatePlayerData - プレイヤーデータ更新関数
 * @param {Array} props.allCards - 全カードデータ
 * @param {Function} props.setGameState - ゲーム状態変更関数
 * @param {Function} props.setMerchantGuildInitialTab - 商人ギルド初期タブ設定
 * @param {Function} props.restAndAdvanceDay - 休息して次の日へ進む関数
 * @param {boolean} props.showRestDialog - 休息完了ダイアログ表示
 * @param {Function} props.setShowRestDialog - 休息完了ダイアログ表示設定
 * @param {Object|null} props.investmentResultData - 投資処理結果
 * @param {Function} props.setInvestmentResultData - 投資処理結果設定
 * @param {Object} props.authState - 認証状態 (useAuth から)
 */
const MenuScreen = ({
  isMobile,
  playerData,
  updatePlayerData,
  allCards,
  setGameState,
  setMerchantGuildInitialTab,
  restAndAdvanceDay,
  showRestDialog,
  setShowRestDialog,
  investmentResultData,
  setInvestmentResultData,
  authState,
}) => {
  // ========================================
  // State (内部管理のみ)
  // ========================================
  const [showDebtStatus, setShowDebtStatus] = useState(false);
  const [showMarketAnalysis, setShowMarketAnalysis] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [longPressSubmenu, setLongPressSubmenu] = useState(null);
  const [highlightedSubmenuIndex, setHighlightedSubmenuIndex] = useState(-1);

  // ========================================
  // Refs
  // ========================================
  const longPressTimerRef = useRef(null);
  const touchStartPosRef = useRef({ x: 0, y: 0 });
  const isDraggingSubmenuRef = useRef(false);

  // ========================================
  // 計算値
  // ========================================
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  const currentDay = playerData?.market?.currentDay || 1;
  const currentWeekday = weekdays[currentDay % 7];
  const vibrationEnabled = playerData?.settings?.vibrationEnabled ?? true;

  // ========================================
  // ヘルパー関数
  // ========================================
  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);

    if (diffMin < 60) {
      return `${diffMin}分前`;
    } else if (diffHour < 24) {
      return `${diffHour}時間前`;
    } else {
      const diffDay = Math.floor(diffHour / 24);
      return `${diffDay}日前`;
    }
  };

  // ========================================
  // 商人ギルド遷移ヘルパー
  // ========================================
  const goToMerchantGuild = (tab = 'merchant') => {
    setMerchantGuildInitialTab(tab);
    setGameState('merchantGuild');
  };

  // ========================================
  // フッタータブ定義
  // ========================================
  const footerTabs = [
    { id: 'duel', icon: '⚔️', label: 'デュエル', action: () => setGameState('duelSetup') },
    { id: 'adventure', icon: '🗺️', label: '冒険', action: () => setGameState('adventure') },
    { id: 'twinKey', icon: '🚪', label: '異界', action: () => setGameState('twinKey') },
    { id: 'draft', icon: '📋', label: 'ドラフト', action: () => setGameState('draft') },
    {
      id: 'merchant',
      icon: '🏪',
      label: '商人',
      action: () => goToMerchantGuild('merchant'),
      hasSubmenu: true,
      submenu: [
        { icon: '🎴', label: 'パック', action: () => goToMerchantGuild('pack') },
        { icon: '🛒', label: '商人', action: () => goToMerchantGuild('merchant') },
        { icon: '🏆', label: '大会', action: () => goToMerchantGuild('tournament') },
        { icon: '📊', label: '投資', action: () => goToMerchantGuild('investment') },
        { icon: '📈', label: '市場', action: () => goToMerchantGuild('market') },
      ],
    },
    {
      id: 'cards',
      icon: '📦',
      label: 'カード',
      action: () => setGameState('collection'),
      hasSubmenu: true,
      submenu: [
        { icon: '📚', label: 'コレクション', action: () => setGameState('collection') },
        { icon: '🃏', label: 'マイデッキ', action: () => setGameState('deckList') },
        { icon: '📖', label: '図鑑', action: () => setGameState('cardDex') },
      ],
    },
    {
      id: 'settings',
      icon: '⚙️',
      label: '設定',
      action: () => {},
      hasSubmenu: true,
      submenu: [
        { icon: '👤', label: 'アカウント', action: () => setShowAccountModal(true) },
        { icon: '🤖', label: 'AI分析', action: () => setGameState('aiAnalysis') },
        { icon: '🌙', label: '休む', action: handleRest },
        { icon: '←', label: 'タイトル', action: () => setGameState('title') },
      ],
    },
  ];

  // ========================================
  // 休息処理
  // ========================================
  function handleRest() {
    // 親の restAndAdvanceDay が差し押さえなしの場合に setShowRestDialog(true) を呼ぶ
    restAndAdvanceDay();
  }

  // ========================================
  // 長押しサブメニュー処理
  // ========================================
  const handleLongPressStart = (e, tab) => {
    if (!tab.hasSubmenu) return;
    const touch = e.touches ? e.touches[0] : e;
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
    isDraggingSubmenuRef.current = false;
    const rect = e.currentTarget.getBoundingClientRect();
    longPressTimerRef.current = setTimeout(() => {
      if (vibrationEnabled && navigator.vibrate) {
        navigator.vibrate(50);
      }
      setLongPressSubmenu({
        tab: tab.id,
        items: tab.submenu,
        position: { x: rect.left + rect.width / 2, y: rect.top },
      });
      setHighlightedSubmenuIndex(-1);
    }, 500);
  };

  const calculateSubmenuIndex = (clientY) => {
    if (!longPressSubmenu) return -1;
    const submenuItems = longPressSubmenu.items;
    const itemHeight = 44;
    const submenuBottom = window.innerHeight - 80;
    const submenuTop = submenuBottom - (submenuItems.length * itemHeight);
    const relativeY = clientY - submenuTop;
    const index = Math.floor(relativeY / itemHeight);
    if (index >= 0 && index < submenuItems.length) {
      return index;
    }
    return -1;
  };

  const handleLongPressMove = (e) => {
    if (!longPressSubmenu) return;
    const touch = e.touches ? e.touches[0] : e;
    const index = calculateSubmenuIndex(touch.clientY);
    if (index >= 0) {
      isDraggingSubmenuRef.current = true;
    }
    setHighlightedSubmenuIndex(index);
  };

  const handleLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (longPressSubmenu && highlightedSubmenuIndex >= 0) {
      const selectedItem = longPressSubmenu.items[highlightedSubmenuIndex];
      if (selectedItem) {
        selectedItem.action();
      }
      setLongPressSubmenu(null);
      setHighlightedSubmenuIndex(-1);
    }
  };

  const handleSubmenuItemTap = (item) => {
    item.action();
    setLongPressSubmenu(null);
    setHighlightedSubmenuIndex(-1);
    isDraggingSubmenuRef.current = false;
  };

  const handleSubmenuClose = () => {
    setLongPressSubmenu(null);
    setHighlightedSubmenuIndex(-1);
    isDraggingSubmenuRef.current = false;
  };

  const handleTabClick = (tab) => {
    if (!longPressSubmenu) {
      tab.action();
    }
  };

  const handleContextMenu = (e, tab) => {
    if (!tab.hasSubmenu) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setLongPressSubmenu({
      tab: tab.id,
      items: tab.submenu,
      position: { x: rect.left + rect.width / 2, y: rect.top },
    });
  };

  // ========================================
  // useEffect: サブメニュータッチイベント
  // ========================================
  useEffect(() => {
    if (!longPressSubmenu) return;

    const handleTouchMove = (e) => {
      if (!longPressSubmenu) return;
      const touch = e.touches[0];
      if (touch) {
        const submenuItems = longPressSubmenu.items;
        const itemHeight = 44;
        const submenuBottom = window.innerHeight - 80;
        const submenuTop = submenuBottom - (submenuItems.length * itemHeight);
        const relativeY = touch.clientY - submenuTop;
        const index = Math.floor(relativeY / itemHeight);

        if (index >= 0 && index < submenuItems.length) {
          isDraggingSubmenuRef.current = true;
        }
        setHighlightedSubmenuIndex(index >= 0 && index < submenuItems.length ? index : -1);
      }
    };

    const handleTouchEnd = (e) => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }

      if (isDraggingSubmenuRef.current && highlightedSubmenuIndex >= 0 && longPressSubmenu) {
        const selectedItem = longPressSubmenu.items[highlightedSubmenuIndex];
        if (selectedItem) {
          selectedItem.action();
        }
      }

      setLongPressSubmenu(null);
      setHighlightedSubmenuIndex(-1);
      isDraggingSubmenuRef.current = false;
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [longPressSubmenu, highlightedSubmenuIndex]);

  // ========================================
  // スタイル定義
  // ========================================
  const sectionStyle = {
    background: 'rgba(30,30,60,0.6)',
    borderRadius: '12px',
    border: '1px solid rgba(107,76,230,0.3)',
    padding: isMobile ? '12px' : '20px 24px',
    width: '100%',
    boxSizing: 'border-box',
  };

  const actionCardStyle = (color1, color2) => ({
    padding: '20px',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '16px',
    background: `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`,
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    minHeight: '100px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
  });

  const subButtonStyle = (color1, color2) => ({
    padding: '12px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
    background: `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
  });

  const sectionTitleStyle = {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#e0e0e0',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const buttonGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
  };

  const guildButtonStyle = (color1, color2) => ({
    padding: '12px 8px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '12px',
    background: `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`,
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  });

  // ========================================
  // 借金返済画面表示
  // ========================================
  if (showDebtStatus) {
    const currentBattleNumber = playerData?.market?.currentDay || 1;
    return (
      <DebtStatus
        playerData={playerData}
        updatePlayerData={updatePlayerData}
        currentBattleNumber={currentBattleNumber}
        onClose={() => setShowDebtStatus(false)}
      />
    );
  }

  // ========================================
  // 共通ダイアログ群
  // ========================================
  const renderDialogs = () => (
    <>
      {/* 休息完了ダイアログ */}
      {showRestDialog && (
        <div style={styles.modal}>
          <div style={{
            ...styles.modalContent,
            textAlign: 'center',
            maxWidth: '400px',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌙</div>
            <h2 style={{ color: '#88ccff', fontSize: '24px', marginBottom: '12px' }}>
              休息完了
            </h2>
            <p style={{ color: '#a0a0a0', fontSize: '16px', marginBottom: '8px' }}>
              ゆっくり休んで体力を回復した...
            </p>
            <div style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#ffd700',
              padding: '12px 24px',
              background: 'rgba(255,215,0,0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(255,215,0,0.3)',
              marginBottom: '20px',
            }}>
              📅 {currentDay} 日目（{currentWeekday}）
            </div>
            <p style={{ color: '#888', fontSize: '13px', marginBottom: '20px' }}>
              ※ 休息では対戦報酬は得られません
            </p>
            <button
              onClick={() => setShowRestDialog(false)}
              style={{ ...styles.actionButton, fontSize: '16px', padding: '12px 32px' }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* 投資処理結果ダイアログ */}
      {investmentResultData && (
        <div style={styles.modal}>
          <div style={{
            ...styles.modalContent,
            textAlign: 'center',
            maxWidth: '500px',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {investmentResultData.debtGenerated > 0 ? '⚠️' :
                investmentResultData.fundLiquidations.length > 0 || investmentResultData.fxLiquidations.length > 0 ? '📉' : '📊'}
            </div>
            <h2 style={{
              color: investmentResultData.debtGenerated > 0 ? '#ff6b6b' : '#88ccff',
              fontSize: '24px',
              marginBottom: '12px',
            }}>
              投資処理通知
            </h2>

            {/* 自動注文 */}
            {investmentResultData.autoOrders.length > 0 && (
              <div style={{
                background: 'rgba(100,200,100,0.1)',
                border: '1px solid rgba(100,200,100,0.3)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px',
                textAlign: 'left',
              }}>
                <div style={{ color: '#88ff88', fontWeight: 'bold', marginBottom: '8px' }}>
                  自動注文が発動しました
                </div>
                {investmentResultData.autoOrders.map((order, idx) => (
                  <div key={idx} style={{ color: '#a0a0a0', fontSize: '14px', marginBottom: '4px' }}>
                    {order.type === 'stop_loss' ? '🛑 SL発動' : '🎯 TP発動'}: {order.fundId}
                    <span style={{ color: order.pnl >= 0 ? '#88ff88' : '#ff6b6b', marginLeft: '8px' }}>
                      {order.pnl >= 0 ? '+' : ''}{order.pnl.toLocaleString()}G
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* ファンドロスカット */}
            {investmentResultData.fundLiquidations.length > 0 && (
              <div style={{
                background: 'rgba(255,100,100,0.1)',
                border: '1px solid rgba(255,100,100,0.3)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px',
                textAlign: 'left',
              }}>
                <div style={{ color: '#ff6b6b', fontWeight: 'bold', marginBottom: '8px' }}>
                  ファンドがロスカットされました
                </div>
                {investmentResultData.fundLiquidations.map((item, idx) => (
                  <div key={idx} style={{ color: '#a0a0a0', fontSize: '14px', marginBottom: '4px' }}>
                    📉 {item.fundId} ({item.leverage}倍レバ) - 維持率{item.marginRatio}%
                  </div>
                ))}
              </div>
            )}

            {/* FXロスカット */}
            {investmentResultData.fxLiquidations.length > 0 && (
              <div style={{
                background: 'rgba(255,100,100,0.1)',
                border: '1px solid rgba(255,100,100,0.3)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px',
                textAlign: 'left',
              }}>
                <div style={{ color: '#ff6b6b', fontWeight: 'bold', marginBottom: '8px' }}>
                  FXポジションがロスカットされました
                </div>
                {investmentResultData.fxLiquidations.map((pos, idx) => {
                  const pairInfo = FX_PAIRS[pos.pairId];
                  return (
                    <div key={idx} style={{ color: '#a0a0a0', fontSize: '14px', marginBottom: '4px' }}>
                      📉 {pairInfo?.symbol || ''} {pairInfo?.name || pos.pairId} ({pos.leverage}倍レバ)
                      <span style={{ color: '#ff6b6b', marginLeft: '8px' }}>
                        {pos.pnl.toLocaleString()}G
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 借金発生 */}
            {investmentResultData.debtGenerated > 0 && (
              <div style={{
                background: 'rgba(255,50,50,0.15)',
                border: '1px solid rgba(255,50,50,0.4)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px',
              }}>
                <div style={{ color: '#ff4444', fontWeight: 'bold', fontSize: '18px' }}>
                  💀 借金が発生しました: {investmentResultData.debtGenerated.toLocaleString()}G
                </div>
                <div style={{ color: '#ff8888', fontSize: '13px', marginTop: '4px' }}>
                  期限内に返済しないとカードが差し押さえられます
                </div>
              </div>
            )}

            <button
              onClick={() => setInvestmentResultData(null)}
              style={{
                ...styles.actionButton,
                fontSize: '16px',
                padding: '12px 32px',
                marginTop: '8px',
              }}
            >
              確認
            </button>
          </div>
        </div>
      )}

      {/* 市場分析モーダル */}
      {showMarketAnalysis && (
        <MarketAnalysis
          marketState={playerData?.market}
          allCards={allCards}
          assetHistory={playerData?.assetHistory}
          onClose={() => setShowMarketAnalysis(false)}
        />
      )}

      {/* アカウントモーダル */}
      {authState && (
        <AccountModal
          isOpen={showAccountModal}
          onClose={() => setShowAccountModal(false)}
          isAuthenticated={authState.isAuthenticated}
          isAnonymous={authState.isAnonymous}
          session={authState.session}
          profile={authState.profile}
          signUpWithEmail={authState.signUpWithEmail}
          signInWithEmail={authState.signInWithEmail}
          linkAccount={authState.linkAccount}
          signOut={authState.signOut}
          loading={authState.loading}
        />
      )}
    </>
  );

  // ========================================
  // 借金警告セクション
  // ========================================
  const renderDebtWarning = () => {
    if (!playerData?.investmentData?.debt || !hasDebt(playerData.investmentData.debt)) {
      return null;
    }
    const currentBattleNumber = playerData?.market?.currentDay || 1;
    const debtSummary = getDebtSummary(playerData.investmentData.debt, currentBattleNumber);

    if (isMobile) {
      return (
        <button
          onClick={() => setShowDebtStatus(true)}
          style={{
            ...sectionStyle,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, rgba(255,50,50,0.15) 0%, rgba(180,30,30,0.2) 100%)',
            border: '2px solid rgba(255,100,100,0.5)',
          }}
        >
          <span style={{ fontSize: '24px' }}>💸</span>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#ff6b6b' }}>
              借金: {debtSummary.amount.toLocaleString()}G
            </div>
            <div style={{ fontSize: '11px', color: debtSummary.isOverdue ? '#f44336' : '#aaa' }}>
              {debtSummary.isOverdue ? '⚠️ 期限切れ' : `あと${debtSummary.remainingDays}戦`}
            </div>
          </div>
          <span style={{ fontSize: '12px', color: '#ff6b6b' }}>→</span>
        </button>
      );
    }

    // PC版
    return (
      <button onClick={() => setShowDebtStatus(true)} style={{
        ...sectionStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '16px 20px',
        cursor: 'pointer',
        background: 'linear-gradient(135deg, rgba(255,50,50,0.15) 0%, rgba(180,30,30,0.2) 100%)',
        border: '2px solid rgba(255,100,100,0.5)',
        animation: debtSummary.isOverdue || debtSummary.remainingDays <= 2 ? 'pulse 1.5s infinite' : 'none',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '28px' }}>💸</span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#ff6b6b' }}>
              借金: {debtSummary.amount.toLocaleString()}G
            </span>
            <span style={{ fontSize: '12px', color: debtSummary.isOverdue ? '#f44336' : debtSummary.remainingDays <= 2 ? '#ff9800' : '#aaa' }}>
              {debtSummary.isOverdue ? '⚠️ 期限切れ！差し押さえ対象' : `返済期限まで あと${debtSummary.remainingDays}戦`}
            </span>
          </div>
        </div>
        <span style={{ padding: '8px 16px', borderRadius: '6px', background: 'linear-gradient(135deg, #ff6b6b 0%, #cc4444 100%)', color: '#fff', fontWeight: 'bold', fontSize: '13px' }}>
          返済する →
        </span>
      </button>
    );
  };

  // ========================================
  // 入荷通知セクション
  // ========================================
  const renderWishlistNotification = () => {
    const wishlist = playerData?.merchantData?.wishlist || [];
    const todayStock = playerData?.merchantData?.todayStock;
    const todayAppearances = playerData?.merchantData?.todayAppearances;

    if (wishlist.length === 0 || !todayStock?.stocks) return null;

    const availableMerchantNames = new Set(['マルクス']);
    if (todayAppearances) {
      (todayAppearances.attribute || []).forEach(name => availableMerchantNames.add(name));
      (todayAppearances.collector || []).forEach(name => availableMerchantNames.add(name));
      if (todayAppearances.dark) availableMerchantNames.add('名無し');
      if (todayAppearances.traveler) availableMerchantNames.add('ゼルヴァン');
    }

    const merchantsWithWishlistItems = [];
    for (const [merchantName, stock] of Object.entries(todayStock.stocks)) {
      if (availableMerchantNames.has(merchantName) && stock && stock.some(item => wishlist.includes(item.cardId))) {
        merchantsWithWishlistItems.push(merchantName);
      }
    }

    if (merchantsWithWishlistItems.length === 0) return null;

    if (isMobile) {
      return (
        <button
          onClick={() => goToMerchantGuild('merchant')}
          style={{
            ...sectionStyle,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, rgba(255,215,0,0.15) 0%, rgba(255,165,0,0.2) 100%)',
            border: '2px solid rgba(255,215,0,0.5)',
          }}
        >
          <span style={{ fontSize: '24px' }}>⭐</span>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#ffd700' }}>入荷通知</div>
            <div style={{ fontSize: '11px', color: '#c0a060' }}>{merchantsWithWishlistItems.join('、')}</div>
          </div>
          <span style={{ fontSize: '12px', color: '#ffd700' }}>→</span>
        </button>
      );
    }

    // PC版
    return (
      <button onClick={() => goToMerchantGuild('merchant')} style={{
        ...sectionStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '16px 20px',
        cursor: 'pointer',
        background: 'linear-gradient(135deg, rgba(255,215,0,0.15) 0%, rgba(255,165,0,0.2) 100%)',
        border: '2px solid rgba(255,215,0,0.5)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '28px' }}>⭐</span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#ffd700' }}>欲しいものリストの商品が入荷！</span>
            <span style={{ fontSize: '12px', color: '#c0a060' }}>{merchantsWithWishlistItems.join('、')} にて販売中</span>
          </div>
        </div>
        <span style={{ padding: '8px 16px', borderRadius: '6px', background: 'linear-gradient(135deg, #ffd700 0%, #ff9500 100%)', color: '#1a1a2e', fontWeight: 'bold', fontSize: '13px' }}>
          確認する →
        </span>
      </button>
    );
  };

  // ========================================
  // 市場ニュースセクション（モバイル版）
  // ========================================
  const renderMarketNewsMobile = () => {
    if (!playerData?.market) return null;

    const msiData = getMarketIndexChartData(playerData.market.priceHistory);
    // サーバーデータを使用（ローカル生成の代わり）
    const serverNews = playerData.market.serverNews || [];
    const weeklyTrend = playerData.market.serverActiveTrend;
    const spotlightEvent = playerData.market.serverSpotlightEvent;

    return (
      <button
        onClick={() => {
          setMerchantGuildInitialTab('market');
          setGameState('merchantGuild');
        }}
        style={{
          ...sectionStyle,
          padding: '10px 12px',
          background: 'linear-gradient(135deg, rgba(255,215,0,0.08) 0%, rgba(255,165,0,0.05) 100%)',
          border: '1px solid rgba(255,215,0,0.3)',
          cursor: 'pointer',
          textAlign: 'left',
          width: '100%',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#ffd700' }}>📰 市場</span>
            <span style={{ fontSize: '12px', color: '#ffd700', fontWeight: 'bold' }}>
              MSI {msiData.currentPrice?.toLocaleString() || '---'}
            </span>
            {msiData.changePercent !== undefined && (
              <span style={{
                fontSize: '11px',
                color: getTrendColor(msiData.changePercent),
                fontWeight: 'bold',
              }}>
                {getTrendIcon(msiData.changePercent)} {msiData.changePercent > 0 ? '+' : ''}{msiData.changePercent}%
              </span>
            )}
          </div>
          <span style={{ fontSize: '12px', color: '#ffd700' }}>→</span>
        </div>
        {spotlightEvent && (
          <div style={{ fontSize: '11px', color: '#ffd700', marginBottom: '4px', background: 'rgba(255,215,0,0.1)', padding: '4px 6px', borderRadius: '4px' }}>
            <span style={{ marginRight: '4px' }}>⭐</span>
            <span style={{ fontWeight: 'bold' }}>[{spotlightEvent.title}]</span>
            <span style={{ marginLeft: '4px', color: '#fff' }}>「{spotlightEvent.cardName}」</span>
          </div>
        )}
        {weeklyTrend && (
          <div style={{ fontSize: '11px', color: '#d0d0d0', marginBottom: '4px' }}>
            <span style={{ color: '#9d4ce6' }}>📈</span> {weeklyTrend.description?.slice(0, 20)}...
          </div>
        )}
        {serverNews.length > 0 && (
          <div style={{ fontSize: '11px', color: '#d0d0d0' }}>
            <span style={{ color: '#ffd700' }}>📰</span> {serverNews[0].text?.slice(0, 25)}...
          </div>
        )}
      </button>
    );
  };

  // ========================================
  // 市場ニュースセクション（PC版）
  // ========================================
  const renderMarketNewsPC = () => {
    if (!playerData?.market) return null;

    const msiData = getMarketIndexChartData(playerData.market.priceHistory);
    // サーバーデータを使用（ローカル生成の代わり）
    const serverNews = playerData.market.serverNews || [];
    const weeklyTrend = playerData.market.serverActiveTrend;
    const spotlightEvent = playerData.market.serverSpotlightEvent;
    const overnightVolatility = playerData.market.serverOvernightVolatility;
    const newsHistory = playerData.market.serverNewsHistory || [];

    const getConditionColor = (condition) => {
      if (condition === '好調' || condition === '上昇') return '#4caf50';
      if (condition === '不調' || condition === '下落') return '#f44336';
      return '#888';
    };

    const newsItemStyle = {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      fontSize: '13px',
      color: '#d0d0d0',
      padding: '6px 0',
      borderBottom: '1px solid rgba(255,215,0,0.1)',
    };

    return (
      <div style={{
        ...sectionStyle,
        padding: '16px 20px',
        background: 'linear-gradient(135deg, rgba(255,215,0,0.08) 0%, rgba(255,165,0,0.05) 100%)',
        border: '1px solid rgba(255,215,0,0.3)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px',
          paddingBottom: '8px',
          borderBottom: '1px solid rgba(255,215,0,0.2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#ffd700' }}>📰 市場ニュース</span>
            <span style={{ fontSize: '13px', color: '#ffd700', fontWeight: 'bold' }}>
              MSI {msiData.currentPrice?.toLocaleString() || '---'}
            </span>
            {msiData.changePercent !== undefined && (
              <span style={{ fontSize: '12px', color: getTrendColor(msiData.changePercent), fontWeight: 'bold' }}>
                {getTrendIcon(msiData.changePercent)} {msiData.changePercent > 0 ? '+' : ''}{msiData.changePercent}%
              </span>
            )}
            {msiData.marketCondition && (
              <span style={{
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '4px',
                background: 'rgba(0,0,0,0.3)',
                color: getConditionColor(msiData.marketCondition),
              }}>
                {msiData.marketCondition}
              </span>
            )}
          </div>
          <button
            onClick={() => setShowMarketAnalysis(true)}
            style={{
              padding: '4px 10px',
              borderRadius: '4px',
              border: '1px solid rgba(255,215,0,0.4)',
              background: 'rgba(255,215,0,0.1)',
              color: '#ffd700',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 'bold',
            }}
          >詳細 →</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {weeklyTrend && (
            <div style={newsItemStyle}>
              <span style={{ color: '#9d4ce6', flexShrink: 0 }}>📈</span>
              <span style={{ flex: 1 }}>
                <span style={{ color: '#a0a0e0', marginRight: '6px' }}>[週間]</span>
                {weeklyTrend.description}
              </span>
              {weeklyTrend.effects?.[0] && (
                <span style={{
                  color: weeklyTrend.effects[0].modifier > 0 ? '#4caf50' : '#f44336',
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}>
                  {weeklyTrend.effects[0].modifier > 0 ? '+' : ''}{weeklyTrend.effects[0].modifier}%
                </span>
              )}
            </div>
          )}
          {/* サーバーニュース一覧 */}
          {serverNews.slice(0, 3).map((news, index) => (
            <div key={index} style={{
              ...newsItemStyle,
              borderBottom: (index === serverNews.slice(0, 3).length - 1 && newsHistory.length === 0 && !spotlightEvent) ? 'none' : undefined,
            }}>
              <span style={{ color: index === 0 ? '#ffd700' : '#6b9eff', flexShrink: 0 }}>
                {index === 0 ? '📰' : '📋'}
              </span>
              <span style={{ flex: 1 }}>
                <span style={{ color: index === 0 ? '#ffd700' : '#6b9eff', marginRight: '6px' }}>
                  [{index === 0 ? '本日' : '速報'}]
                </span>
                「{news.text}」
              </span>
              {news.modifier !== undefined && news.modifier !== 0 && (
                <span style={{
                  color: news.modifier > 0 ? '#4caf50' : '#f44336',
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}>
                  {news.modifier > 0 ? '+' : ''}{news.modifier}%
                </span>
              )}
            </div>
          ))}
          {/* 過去ニュース履歴（最新3件） */}
          {newsHistory.slice(0, 3).map((entry, historyIndex) => {
            const mainNews = entry.news?.[0];
            if (!mainNews) return null;
            const historyDate = entry.timestamp ? new Date(entry.timestamp) : null;
            const timeAgo = historyDate ? formatTimeAgo(historyDate) : '';
            const isDaily = entry.updateType === 'daily';
            return (
              <div key={`history-${historyIndex}`} style={{
                ...newsItemStyle,
                borderBottom: (historyIndex === newsHistory.slice(0, 3).length - 1 && !spotlightEvent) ? 'none' : undefined,
                opacity: 0.8,
              }}>
                <span style={{ color: isDaily ? '#8a8aff' : '#ff8a8a', flexShrink: 0 }}>
                  {isDaily ? '📅' : '📣'}
                </span>
                <span style={{ flex: 1 }}>
                  <span style={{ color: '#888', marginRight: '6px', fontSize: '11px' }}>
                    [{isDaily ? 'デイリー' : 'メジャー'}・{timeAgo}]
                  </span>
                  「{mainNews.text}」
                </span>
                {mainNews.modifier !== undefined && mainNews.modifier !== 0 && (
                  <span style={{
                    color: mainNews.modifier > 0 ? '#4caf50' : '#f44336',
                    fontWeight: 'bold',
                    flexShrink: 0,
                    opacity: 0.7,
                  }}>
                    {mainNews.modifier > 0 ? '+' : ''}{mainNews.modifier}%
                  </span>
                )}
              </div>
            );
          })}
          {spotlightEvent && (
            <div style={{
              ...newsItemStyle,
              borderBottom: overnightVolatility ? undefined : 'none',
              background: 'rgba(255,215,0,0.1)',
              margin: '4px -8px 0',
              padding: '8px',
            }}>
              <span style={{ color: '#ffd700', flexShrink: 0, fontSize: '16px' }}>🌟</span>
              <span style={{ flex: 1 }}>
                <span style={{ color: '#ffd700', marginRight: '6px', fontWeight: 'bold' }}>[{spotlightEvent.title}]</span>
                <span style={{ color: '#fff' }}>「{spotlightEvent.cardName}」</span>
                <span style={{ color: '#aaa', marginLeft: '4px' }}>が話題!</span>
              </span>
              <span style={{
                color: '#ff6b6b',
                fontWeight: 'bold',
                flexShrink: 0,
                background: 'rgba(255,100,100,0.2)',
                padding: '2px 6px',
                borderRadius: '4px',
              }}>×10</span>
            </div>
          )}
          {overnightVolatility && (
            <div style={{
              ...newsItemStyle,
              borderBottom: 'none',
              background: overnightVolatility.direction === 'up' ? 'rgba(76,175,80,0.1)' : 'rgba(244,67,54,0.1)',
              margin: '4px -8px 0',
              padding: '8px',
            }}>
              <span style={{
                color: overnightVolatility.direction === 'up' ? '#4caf50' : '#f44336',
                flexShrink: 0,
              }}>⚡</span>
              <span style={{ flex: 1 }}>
                <span style={{ color: '#ff9800', marginRight: '6px', fontWeight: 'bold' }}>[夜間]</span>
                <span style={{ color: '#d0d0d0' }}>{overnightVolatility.text}</span>
              </span>
              <span style={{
                color: overnightVolatility.direction === 'up' ? '#4caf50' : '#f44336',
                fontWeight: 'bold',
                flexShrink: 0,
              }}>
                {overnightVolatility.spikePercent > 0 ? '+' : ''}{overnightVolatility.spikePercent}%
              </span>
            </div>
          )}
          {!weeklyTrend && serverNews.length === 0 && !spotlightEvent && !overnightVolatility && (
            <div style={{ color: '#888', fontSize: '12px', textAlign: 'center', padding: '8px 0' }}>
              現在ニュースはありません
            </div>
          )}
        </div>
      </div>
    );
  };

  // ========================================
  // 長押しサブメニューオーバーレイ（モバイル共通）
  // ========================================
  const renderLongPressSubmenu = () => {
    if (!longPressSubmenu) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 150,
        }}
        onMouseMove={handleLongPressMove}
        onMouseUp={() => handleLongPressEnd(true)}
        onClick={handleSubmenuClose}
      >
        <div
          style={{
            position: 'fixed',
            left: Math.max(10, Math.min(longPressSubmenu.position.x - 80, window.innerWidth - 170)),
            bottom: 80,
            width: '160px',
            background: 'linear-gradient(180deg, rgba(30,30,60,0.98) 0%, rgba(20,20,40,0.98) 100%)',
            borderRadius: '12px',
            border: '2px solid #6b4ce6',
            boxShadow: '0 -4px 20px rgba(107,76,230,0.5)',
            zIndex: 200,
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          {longPressSubmenu.items.map((item, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: highlightedSubmenuIndex === index ? '#fff' : '#a0a0c0',
                background: highlightedSubmenuIndex === index
                  ? 'linear-gradient(90deg, rgba(107,76,230,0.6) 0%, rgba(107,76,230,0.3) 100%)'
                  : 'transparent',
                borderBottom: index < longPressSubmenu.items.length - 1 ? '1px solid rgba(107,76,230,0.2)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.1s ease',
              }}
              onClick={() => handleSubmenuItemTap(item)}
              onTouchEnd={(e) => {
                e.stopPropagation();
                handleSubmenuItemTap(item);
              }}
              onMouseEnter={() => setHighlightedSubmenuIndex(index)}
            >
              <span style={{ fontSize: '18px' }}>{item.icon}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ========================================
  // モバイル用レイアウト
  // ========================================
  if (isMobile) {
    return (
      <div style={{
        ...styles.container,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}>
        {/* 固定ヘッダー */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(90deg, rgba(20,20,50,0.98) 0%, rgba(40,20,60,0.98) 50%, rgba(20,20,50,0.98) 100%)',
          borderBottom: '2px solid #6b4ce6',
          padding: '10px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 20px rgba(107,76,230,0.3)',
          zIndex: 100,
        }}>
          <div style={{
            fontSize: '16px',
            fontWeight: 'bold',
            background: 'linear-gradient(90deg, #9d4ce6, #6b9eff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Magic Spirit
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {playerData && (
              <>
                <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#ffd700' }}>
                  💰 {currencyManager.formatGold(playerData.gold)}
                </span>
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#88ccff' }}>
                  📅 {currentDay}日目
                </span>
              </>
            )}
            {/* アカウントボタン */}
            <button
              onClick={() => setShowAccountModal(true)}
              style={{
                background: authState?.isAnonymous
                  ? 'rgba(255,152,0,0.2)'
                  : 'rgba(76,175,80,0.2)',
                border: `1px solid ${authState?.isAnonymous ? 'rgba(255,152,0,0.5)' : 'rgba(76,175,80,0.5)'}`,
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
                fontSize: '14px',
              }}
              title={authState?.isAnonymous ? 'ゲストモード - タップして登録' : 'アカウント設定'}
            >
              {authState?.isAnonymous ? '👤' : '✅'}
            </button>
          </div>
        </div>

        {/* スクロール可能なメインコンテンツ */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          paddingTop: '60px',
          paddingBottom: '80px',
          padding: '60px 12px 80px 12px',
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            maxWidth: '400px',
            margin: '0 auto',
          }}>
            {renderDebtWarning()}
            {renderWishlistNotification()}
            {renderMarketNewsMobile()}
          </div>
        </div>

        {/* 固定フッター */}
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'space-around',
            padding: '6px 4px',
            paddingBottom: 'calc(6px + env(safe-area-inset-bottom))',
            background: 'linear-gradient(180deg, rgba(20,20,50,0.98) 0%, rgba(10,10,30,0.99) 100%)',
            borderTop: '2px solid #6b4ce6',
            boxShadow: '0 -4px 20px rgba(107,76,230,0.3)',
            zIndex: 100,
          }}
        >
          {footerTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab)}
              onTouchStart={(e) => handleLongPressStart(e, tab)}
              onMouseDown={(e) => handleLongPressStart(e, tab)}
              onContextMenu={(e) => handleContextMenu(e, tab)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '6px 2px',
                border: 'none',
                background: longPressSubmenu?.tab === tab.id
                  ? 'linear-gradient(180deg, rgba(107,76,230,0.4) 0%, rgba(107,76,230,0.2) 100%)'
                  : 'transparent',
                color: longPressSubmenu?.tab === tab.id ? '#fff' : '#808090',
                fontSize: '10px',
                fontWeight: 'bold',
                cursor: 'pointer',
                borderRadius: '8px',
                margin: '0 2px',
                transition: 'all 0.2s ease',
              }}
            >
              <span style={{ fontSize: '20px', marginBottom: '2px' }}>{tab.icon}</span>
              <span>{tab.label}</span>
              {tab.hasSubmenu && (
                <span style={{ fontSize: '6px', color: '#606080', marginTop: '1px' }}>•••</span>
              )}
            </button>
          ))}
        </div>

        {renderLongPressSubmenu()}
        {renderDialogs()}
      </div>
    );
  }

  // ========================================
  // PC用レイアウト（横幅活用）
  // ========================================
  return (
    <div style={{
      ...styles.container,
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      overflow: 'auto',
    }}>
      {/* ヘッダー */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 32px',
        background: 'linear-gradient(90deg, rgba(20,20,50,0.98) 0%, rgba(40,20,60,0.98) 50%, rgba(20,20,50,0.98) 100%)',
        borderBottom: '2px solid #6b4ce6',
        boxShadow: '0 4px 20px rgba(107,76,230,0.3)',
      }}>
        <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          background: 'linear-gradient(90deg, #9d4ce6, #6b9eff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          Magic Spirit
        </div>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          {playerData && (
            <>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#ffd700' }}>
                💰 {currencyManager.formatGold(playerData.gold)}
              </span>
              <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#88ccff' }}>
                📅 {currentDay}日目 ({currentWeekday})
              </span>
            </>
          )}
          {/* アカウントボタン */}
          <button
            onClick={() => setShowAccountModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: authState?.isAnonymous
                ? 'rgba(255,152,0,0.15)'
                : 'rgba(76,175,80,0.15)',
              border: `1px solid ${authState?.isAnonymous ? 'rgba(255,152,0,0.4)' : 'rgba(76,175,80,0.4)'}`,
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 'bold',
              color: authState?.isAnonymous ? '#ff9800' : '#4caf50',
            }}
          >
            <span>{authState?.isAnonymous ? '👤' : '✅'}</span>
            <span>
              {authState?.isAnonymous
                ? 'ゲスト'
                : (authState?.profile?.display_name || 'ログイン中')}
            </span>
          </button>
        </div>
      </div>

      {/* メインコンテンツ - 3カラム */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '1fr 1.2fr 1fr',
        gap: '24px',
        padding: '24px 32px',
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
      }}>
        {/* 左カラム: 市場ニュース */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {renderMarketNewsPC()}

          {/* 開発ツール */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>🔧 開発ツール</div>
            <button
              onClick={() => setGameState('aiAnalysis')}
              style={{
                ...styles.actionButton,
                background: 'linear-gradient(135deg, #4a90d9 0%, #357abd 100%)',
                fontSize: '14px',
                padding: '12px 24px',
                width: '100%',
              }}
            >🤖 AI分析</button>
            <div style={{ fontSize: '11px', color: '#808080', marginTop: '6px', textAlign: 'center' }}>
              AI同士の対戦をシミュレーション
            </div>
          </div>
        </div>

        {/* 中央カラム: メインアクション */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {renderDebtWarning()}
          {renderWishlistNotification()}

          {/* デュエル */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>⚔️ デュエル</div>
            <button
              onClick={() => setGameState('duelSetup')}
              style={{ ...styles.actionButton, fontSize: '18px', padding: '16px 40px', width: '100%' }}
            >⚔️ デュエル開始</button>
          </div>

          {/* 異界の扉 */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>🚪 異界の扉</div>
            <button
              onClick={() => setGameState('twinKey')}
              style={{
                ...styles.actionButton,
                fontSize: '18px',
                padding: '16px 40px',
                width: '100%',
                background: 'linear-gradient(135deg, #9d4ce6 0%, #6b4ce6 50%, #4c8be6 100%)',
                boxShadow: '0 4px 15px rgba(107,76,230,0.4)',
              }}
            >🔑 ダンジョン探索</button>
            <div style={{ fontSize: '12px', color: '#a0a0c0', marginTop: '8px', textAlign: 'center' }}>
              2枚のカードを「鍵」として異世界に挑戦
            </div>
          </div>

          {/* アドベンチャーモード */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>🗺️ アドベンチャー</div>
            <button
              onClick={() => setGameState('adventure')}
              style={{
                ...styles.actionButton,
                fontSize: '18px',
                padding: '16px 40px',
                width: '100%',
                background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                boxShadow: '0 4px 15px rgba(46,204,113,0.4)',
              }}
            >⚔️ 冒険に出る</button>
            <div style={{ fontSize: '12px', color: '#a0a0c0', marginTop: '8px', textAlign: 'center' }}>
              NPCとカード対戦して報酬を獲得
            </div>
          </div>

          {/* ブースタードラフト */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>📋 ブースタードラフト</div>
            <button
              onClick={() => setGameState('draft')}
              style={{
                ...styles.actionButton,
                fontSize: '18px',
                padding: '16px 40px',
                width: '100%',
                background: 'linear-gradient(135deg, #ff8c00 0%, #ffd700 100%)',
                boxShadow: '0 4px 15px rgba(255,140,0,0.4)',
                color: '#000',
              }}
            >📋 ドラフト開始</button>
            <div style={{ fontSize: '12px', color: '#a0a0c0', marginTop: '8px', textAlign: 'center' }}>
              NPCとカードをドラフトしてデッキを構築
            </div>
          </div>

          {/* 休む */}
          <button
            onClick={handleRest}
            disabled={!playerData}
            style={{
              ...sectionStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px 40px',
              cursor: playerData ? 'pointer' : 'not-allowed',
              fontWeight: 'bold',
              fontSize: '16px',
              color: '#a0c0d0',
              opacity: playerData ? 1 : 0.5,
            }}
            title="対戦せずに次の日へ進みます（報酬なし）"
          >🌙 休む（次の日へ）</button>
        </div>

        {/* 右カラム: ギルド・カード管理 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* 商人ギルド */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>🏪 商人ギルド</div>
            <div style={buttonGridStyle}>
              <button onClick={() => goToMerchantGuild('pack')} style={guildButtonStyle('#6b4ce6', '#9d4ce6')}>
                <span style={{ fontSize: '24px' }}>🎴</span><span>パック</span>
              </button>
              <button onClick={() => goToMerchantGuild('merchant')} style={guildButtonStyle('#8b4513', '#d2691e')}>
                <span style={{ fontSize: '24px' }}>🛒</span><span>商人</span>
              </button>
              <button onClick={() => goToMerchantGuild('tournament')} style={guildButtonStyle('#b8860b', '#daa520')}>
                <span style={{ fontSize: '24px' }}>🏆</span><span>大会</span>
              </button>
              <button onClick={() => goToMerchantGuild('investment')} style={guildButtonStyle('#2e8b57', '#3cb371')}>
                <span style={{ fontSize: '24px' }}>📊</span><span>投資</span>
              </button>
              <button onClick={() => goToMerchantGuild('market')} style={guildButtonStyle('#cd853f', '#daa520')}>
                <span style={{ fontSize: '24px' }}>📈</span><span>市場</span>
              </button>
            </div>
          </div>

          {/* カード管理 */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>📦 カード管理</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={() => setGameState('collection')}
                style={{
                  ...styles.actionButton,
                  background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
                  fontSize: '14px',
                  padding: '12px 24px',
                }}
              >📚 コレクション</button>
              <button
                onClick={() => setGameState('deckList')}
                style={{
                  ...styles.actionButton,
                  background: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)',
                  fontSize: '14px',
                  padding: '12px 24px',
                }}
              >🃏 マイデッキ</button>
              <button
                onClick={() => setGameState('cardDex')}
                style={{
                  ...styles.actionButton,
                  background: 'linear-gradient(135deg, #e6a04c 0%, #e6c84c 100%)',
                  fontSize: '14px',
                  padding: '12px 24px',
                }}
              >📖 図鑑</button>
            </div>
          </div>

          {/* 設定 */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>⚙️ 設定</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={() => setGameState('title')}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(100,100,120,0.5)',
                  background: 'transparent',
                  color: '#888',
                  fontSize: '13px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >← タイトルに戻る</button>
            </div>
          </div>

          <div style={{ color: '#666', fontSize: '12px', textAlign: 'center' }}>
            {allCards.length}枚のカードを読み込み完了
          </div>
        </div>
      </div>

      {renderDialogs()}
    </div>
  );
};

export default MenuScreen;
